import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoShieldCheckmarkOutline, IoLockClosedOutline, IoDocumentTextOutline, IoKeyOutline } from "react-icons/io5";
import Confirmation from "../../common/components/Confirmation";
import { performSessionLogout } from "../../common/utils/authSession";
import { markFamilyPortalConsentConfirmed } from "../../common/utils/familyPortalConsent";
import { acceptPortalConsent } from "../../common/api/familyPortalConsentApi";
import * as Sentry from "@sentry/react";

const CONSENT_SECTIONS = [
  {
    icon: IoShieldCheckmarkOutline,
    title: "Authorized access",
    text: "By accessing the Health Care Concierge Family Portal, I certify that I am the individual associated with this account and understand that Health Care Concierge has granted me access based upon authorization provided by the client or the client's authorized representative.",
  },
  {
    icon: IoDocumentTextOutline,
    title: "Confidential information",
    text: "I understand that information available through this portal is private and confidential and may include caregiver notes, services provided, care tasks, and information regarding the client's health and care.",
  },
  {
    icon: IoKeyOutline,
    title: "Protecting my account",
    text: "I agree to keep my login credentials secure, not permit another person to use my account, and take reasonable measures to protect information I access through the portal. I understand that I may not provide portal access to another person and that Health Care Concierge may suspend or terminate my access if the client's authorization is changed or revoked.",
  },
];

/**
 * Agreement wording, shared by the first-login screen and the profile copy.
 * `accepted` switches the closing statement to the past tense, so the profile
 * copy reads as a record rather than an action still to take.
 */
export const FamilyConsentTerms = ({ accepted = false }) => (
  <>
    <div className="family-consent-sections">
      {CONSENT_SECTIONS.map(({ icon: Icon, title, text }) => (
        <section key={title} className="family-consent-section">
          <span className="family-consent-section-icon" aria-hidden="true">
            <Icon />
          </span>
          <div className="family-consent-section-content">
            <h2 className="family-consent-section-title">{title}</h2>
            <p className="family-consent-section-text">{text}</p>
          </div>
        </section>
      ))}
    </div>

    <p className="family-consent-certify">
      {accepted ? (
        "I certified that I am the authorized individual associated with this account and accepted responsibility for maintaining the confidentiality of the information made available to me."
      ) : (
        <>By selecting &ldquo;I Agree,&rdquo; I certify that I am the authorized individual associated with this account and accept responsibility for maintaining the confidentiality of the information made available to me.</>
      )}
    </p>
  </>
);

/**
 * Confidentiality & Authorized Access agreement. Rendered by FamilyConsentGate
 * in place of the requested family page; `onAccepted` runs once the server has
 * saved the acceptance, which lets the gate mount that page.
 */
const FamilyConsent = ({ onAccepted }) => {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [showDeclinePopup, setShowDeclinePopup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleContinue = async () => {
    if (!agreed || submitting) {
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const result = await acceptPortalConsent();
      if (result.success) {
        markFamilyPortalConsentConfirmed();
        onAccepted?.();
        return;
      }
      setError(result.message || "Unable to save your agreement. Please try again.");
    } catch (err) {
      Sentry.captureException(err);
      setError("Unable to save your agreement. Please try again.");
    }
    setSubmitting(false);
  };

  const handleDeclineConfirm = async (e) => {
    e?.preventDefault?.();
    setShowDeclinePopup(false);
    const isNativeWebView = Boolean(window.flutter_inappwebview?.callHandler);
    await performSessionLogout({ navigate: isNativeWebView ? undefined : navigate });
  };

  return (
    <div className="family-consent-layout">
      <div className="family-consent-page">
        <div className="family-consent-card">
          <div className="family-consent-head">
            <img src={require("../../assets/btTree.png")} className="family-consent-logo" alt="BridgeTree" />
            <span className="family-consent-badge">
              <IoLockClosedOutline aria-hidden="true" />
              Before you continue
            </span>
            <h1 className="family-consent-title">Confidentiality &amp; Authorized Access</h1>
            <p className="family-consent-subtitle">Please read and accept the terms below to access the Family Portal.</p>
          </div>

          <div className="family-consent-body">
            <FamilyConsentTerms />
          </div>

          <div className="family-consent-footer">
            <label className={`family-consent-checkbox${agreed ? " family-consent-checkbox--checked" : ""}`}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>I Agree and Certify</span>
            </label>

            {error ? (
              <p className="family-consent-error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="button" className="family-consent-continue" onClick={handleContinue} disabled={!agreed || submitting}>
              {submitting ? "Saving..." : "Continue"}
            </button>
            <div className="family-consent-decline-row">
              <button type="button" className="family-consent-decline" onClick={() => setShowDeclinePopup(true)}>
                Decline and log out
              </button>
            </div>
          </div>
        </div>
      </div>

      <Confirmation content="You must agree to the confidentiality terms to use the portal. Do you want to log out?" open={showDeclinePopup} closeConfirmation={() => setShowDeclinePopup(false)} processCallback={(e) => handleDeclineConfirm(e)} />
    </div>
  );
};

export default FamilyConsent;
